import { useEffect, useMemo, useState } from 'react'
import { FileText, RefreshCw, ArrowRight, Settings2, Sparkles } from 'lucide-react'

import type { ValidationStageKey, SkerpaGuidanceItem, JourneyPhaseWithMode } from '@ispot/shared'

import { useJourneyStore } from '@/stores/journeyStore'
import { useSessionStore } from '@/stores/sessionStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import {
  useLatestCompletedAnalysis,
  useStartValidationAnalysis,
} from '@/hooks/useValidationAnalysis'
import { useGuidanceItems } from '@/hooks/useGuidance'
import { ValidationRadar } from '@/components/validation/ValidationRadar'
import { classifyBand } from '@/components/validation/ValidationScoreBand'
import { GuidanceDetailPanel } from '@/components/workspace/dashboard/GuidanceDetailPanel'
import { CompactPhaseDetail } from '@/components/workspace/dashboard/CompactPhaseDetail'

const STAGES = [
  { key: 'pe' as ValidationStageKey, shortName: 'Problem', name: 'Problem existence' },
  { key: 'pu' as ValidationStageKey, shortName: 'Urgency', name: 'Problem urgency' },
  { key: 'psf' as ValidationStageKey, shortName: 'Solution', name: 'Problem–solution fit' },
  { key: 'bmf' as ValidationStageKey, shortName: 'Business', name: 'Business model fit' },
  { key: 'pmf' as ValidationStageKey, shortName: 'Market', name: 'Product–market fit' },
]

const EMPTY_PHASE_PROGRESS: Record<string, number> = {}

const PRIORITY_COLOR: Record<string, string> = {
  high: 'var(--color-danger)',
  medium: 'var(--color-warning)',
  low: 'var(--color-fg-muted)',
}

interface CompactModeProps {
  onViewChange?: (view: string) => void
}

export function CompactMode({ onViewChange }: CompactModeProps) {
  const [selectedGuidanceId, setSelectedGuidanceId] = useState<string | null>(null)
  const [expandedPhaseId, setExpandedPhaseId] = useState<string | null>(null)
  const { accountId, projectId } = useSessionStore()
  const { setShowGoalWizard } = useWorkspaceStore()
  const phaseProgress = useJourneyStore(s => s.journey?.phaseProgress ?? EMPTY_PHASE_PROGRESS)
  const {
    fetchJourney, getSelectedGoal, getCurrentPhase,
    getActivePhases, getOverallProgress, isActivityCompleted, isArtifactCompleted,
  } = useJourneyStore()

  const { data: analysis } = useLatestCompletedAnalysis()
  const startAnalysis = useStartValidationAnalysis()
  const { items: guidanceItems } = useGuidanceItems()

  useEffect(() => {
    if (accountId && projectId) void fetchJourney(accountId, projectId)
  }, [accountId, projectId, fetchJourney])

  const scores = useMemo(
    () => STAGES.reduce((acc, s) => {
      acc[s.key] = analysis?.stageScores?.find((x) => x.stageKey === s.key)?.confidenceScore ?? 0
      return acc
    }, {} as Record<ValidationStageKey, number>),
    [analysis],
  )

  const overall = useMemo(() => {
    const vals = STAGES.map((s) => scores[s.key] ?? 0)
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
  }, [scores])

  const { strongest, weakest } = useMemo(() => {
    const sorted = [...STAGES].sort((a, b) => (scores[b.key] ?? 0) - (scores[a.key] ?? 0))
    return { strongest: sorted[0], weakest: sorted[sorted.length - 1] }
  }, [scores])

  const band = analysis ? classifyBand(overall) : null
  const tone = band ? { stroke: band.color, fill: band.fillColor } : undefined

  const lastAnalyzed = analysis?.completedAt
    ? new Date(analysis.completedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null

  const goal = getSelectedGoal()
  const currentPhase = getCurrentPhase()
  const phases = getActivePhases()
  const overallProgress = Math.round(getOverallProgress())
  const expandedPhase: JourneyPhaseWithMode | null = expandedPhaseId
    ? phases.find(p => p.id === expandedPhaseId) ?? null
    : null

  const nextActivityName = useMemo(() => {
    if (!currentPhase) return null
    return currentPhase.activities.find((a) => !isActivityCompleted(a.id))?.name ?? null
  }, [currentPhase, isActivityCompleted])

  const journeyEyebrow = !goal ? 'Get started' : !currentPhase ? 'Journey complete' : `Up next in ${currentPhase.name}`
  const journeyHeading = !goal
    ? 'Pick a journey to set the path.'
    : !currentPhase ? "You've worked through every phase."
      : nextActivityName ?? 'Review the phase and move on.'

  return (
    <div className="flex flex-col" style={{ height: '100%', overflow: 'hidden' }}>

      {/* ── Row 1: 50% — flex row: 3-col grid + optional detail panel ── */}
      <div
        style={{
          height: '50%',
          display: 'flex',
          borderBottom: '1px solid var(--color-border)',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        {/* 3-column grid — shrinks when panel is open */}
        <div style={{ flex: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '30% 40% 30%', overflow: 'hidden' }}>
        {/* Validation text — capped at 30% */}
        <div
          style={{
            borderRight: '1px solid var(--color-border)',
            padding: '24px 28px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <div className="font-mono uppercase text-text-muted" style={{ fontSize: 9.5, letterSpacing: '0.12em', marginBottom: 10 }}>
            Validation analysis{lastAnalyzed ? ` · ${lastAnalyzed}` : ''}
          </div>

          {analysis && band ? (
            <>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.014em', color: 'var(--color-fg)', marginBottom: 6 }}>
                Sits in the{' '}
                <em className="not-italic font-semibold" style={{ color: band.color }}>{band.label}</em>{' '}
                band
              </h2>
              <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--color-fg-secondary)', marginBottom: 12 }}>
                Overall confidence score of <strong style={{ color: 'var(--color-fg)', fontWeight: 500 }}>{overall}%</strong>.<br />
                Strongest on <strong style={{ color: 'var(--color-fg)', fontWeight: 500 }}>{strongest.name}</strong> at {scores[strongest.key]}%.<br />
                Weakest on <strong style={{ color: 'var(--color-fg)', fontWeight: 500 }}>{weakest.name}</strong> at {scores[weakest.key]}%.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {analysis.artifactId && (
                  <button type="button" onClick={() => onViewChange?.('analysis')}
                    className="inline-flex items-center gap-1.5 rounded-md bg-bg-card hover:bg-bg-secondary border border-border-strong transition-colors"
                    style={{ padding: '5px 10px', fontSize: 12, fontWeight: 500, color: 'var(--color-fg)' }}>
                    <FileText className="w-3 h-3" />View report
                  </button>
                )}
                <button type="button" onClick={() => startAnalysis.mutate()} disabled={startAnalysis.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-text text-white hover:bg-[#14242e] disabled:opacity-50 transition-colors"
                  style={{ padding: '5px 10px', fontSize: 12, fontWeight: 500 }}>
                  <RefreshCw className={`w-3 h-3 ${startAnalysis.isPending ? 'animate-spin' : ''}`} />Re-analyze
                </button>
              </div>
              <button type="button" onClick={() => onViewChange?.('analysis')}
                className="inline-flex items-center gap-1 w-fit hover:text-text transition-colors"
                style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-fg-secondary)' }}>
                View full validation report<ArrowRight className="w-3.5 h-3.5 ml-1" />
              </button>
            </>
          ) : (
            <div>
              <p className="text-sm text-text-muted" style={{ marginBottom: 10 }}>No analysis run yet.</p>
              <button type="button" onClick={() => startAnalysis.mutate()} disabled={startAnalysis.isPending}
                className="inline-flex items-center gap-1.5 w-fit rounded-md bg-text text-white hover:bg-[#14242e] disabled:opacity-50 transition-colors"
                style={{ padding: '6px 12px', fontSize: 12, fontWeight: 500 }}>
                <RefreshCw className={`w-3 h-3 ${startAnalysis.isPending ? 'animate-spin' : ''}`} />Run analysis
              </button>
            </div>
          )}
        </div>

        {/* Radar — fills remaining center, constrained max-width */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid var(--color-border)', overflow: 'hidden', padding: '12px 0' }}>
          {analysis && tone ? (
            <div style={{ width: '100%', maxWidth: 380 }}>
              <ValidationRadar stages={STAGES} scores={scores} selectedStage={null} onSelectStage={() => {}} tone={tone} />
            </div>
          ) : (
            <div style={{ width: 260, height: 200, background: 'var(--color-bg-secondary)', borderRadius: 8 }} />
          )}
        </div>

        {/* Next steps — fixed 22% */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div className="flex items-center gap-1.5 font-mono uppercase text-text-muted flex-shrink-0"
            style={{ fontSize: 9.5, letterSpacing: '0.1em', padding: '14px 14px 8px' }}>
            <Sparkles className="w-3 h-3" />Next steps
          </div>
          <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
            {guidanceItems.length === 0
              ? <p className="text-xs text-text-muted" style={{ padding: '4px 14px' }}>No guidance items yet.</p>
              : guidanceItems.slice(0, 8).map((item) => (
                <NextStepItem
                  key={item.id}
                  item={item}
                  isSelected={item.id === selectedGuidanceId}
                  onSelect={() => setSelectedGuidanceId(prev => prev === item.id ? null : item.id)}
                  onViewChange={onViewChange}
                />
              ))}
          </div>
        </div>
        </div>{/* end inner grid */}

        {selectedGuidanceId && guidanceItems.find(i => i.id === selectedGuidanceId) && (
          <GuidanceDetailPanel
            item={guidanceItems.find(i => i.id === selectedGuidanceId)!}
            onClose={() => setSelectedGuidanceId(null)}
            onViewChange={onViewChange}
          />
        )}
      </div>

      {/* ── Row 2: 35% — journey heading + phase grid | optional detail panel ── */}
      <div style={{ height: '35%', display: 'flex', flexShrink: 0, overflow: 'hidden' }}>

        {/* Left: heading + phase grid */}
        <div style={{ flex: 1, minWidth: 0, padding: '20px 28px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
            <div>
              <div className="font-mono uppercase text-text-muted" style={{ fontSize: 9.5, letterSpacing: '0.1em', marginBottom: 4 }}>
                {journeyEyebrow}
              </div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.012em', color: 'var(--color-fg)' }}>
                {journeyHeading}
              </h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginTop: 2 }}>
              <span className="font-mono tabular-nums text-text-muted" style={{ fontSize: 11 }}>Journey · {overallProgress}%</span>
              <button onClick={() => setShowGoalWizard(true)} className="flex items-center gap-1 text-text-secondary hover:text-text transition-colors" style={{ fontSize: 11 }}>
                <Settings2 className="w-3 h-3" />Change
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(phases.length, 1)}, 1fr)`, gap: 0, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
            {phases.map((phase, idx) => {
              const progress = phaseProgress[phase.id] ?? 0
              const isCurrent = phase.id === currentPhase?.id
              const isDone = progress >= 100
              const isExpanded = expandedPhaseId === phase.id
              return (
                <button
                  key={phase.id}
                  type="button"
                  onClick={() => setExpandedPhaseId(prev => prev === phase.id ? null : phase.id)}
                  style={{ paddingLeft: idx === 0 ? 0 : 12, paddingRight: 12, paddingTop: 6, paddingBottom: 6, borderLeft: idx === 0 ? 'none' : '1px solid var(--color-border)', textAlign: 'left', cursor: 'pointer', borderRadius: 4, background: isExpanded ? 'var(--color-bg-secondary)' : undefined, transition: 'background 120ms' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, backgroundColor: phase.color }} />
                    <span style={{ fontSize: 12.5, fontWeight: isCurrent || isDone ? 500 : 400, color: isCurrent || isDone ? 'var(--color-fg)' : 'var(--color-fg-secondary)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      {phase.name}
                    </span>
                  </div>
                  <div style={{ height: 4, borderRadius: 4, background: isDone ? 'var(--color-fg)' : 'var(--color-warm-200)', overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ height: '100%', borderRadius: 4, width: `${Math.min(progress, 100)}%`, backgroundColor: isCurrent ? 'var(--color-accent)' : 'var(--color-fg)', transition: 'width 500ms ease-out' }} />
                  </div>
                  <span className="font-mono tabular-nums" style={{ fontSize: 11, color: isCurrent && !isDone ? 'var(--color-accent)' : 'var(--color-fg-muted)' }}>
                    {isDone ? 'Done' : progress > 0 ? `${Math.round(progress)}%` : 'Not started'}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right: phase detail slide-in panel */}
        {expandedPhase && (
          <div style={{ width: 360, flexShrink: 0, borderLeft: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', padding: '16px 20px', overflowY: 'auto' }}>
            <div className="font-mono text-text-muted uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', marginBottom: 10 }}>
              {expandedPhase.name}
            </div>
            <CompactPhaseDetail
              phase={expandedPhase}
              isActivityCompleted={isActivityCompleted}
              isArtifactCompleted={isArtifactCompleted}
            />
          </div>
        )}
      </div>

      {/* ── Row 3: spacer ── */}
      <div style={{ flex: 1 }} />
    </div>
  )
}

// ─── Next step item with hover-reveal description ───────────────────────────

interface NextStepItemProps {
  item: SkerpaGuidanceItem
  isSelected: boolean
  onSelect: () => void
  onViewChange?: (view: string) => void
}

function NextStepItem({ item, isSelected, onSelect }: NextStepItemProps) {
  return (
    <div
      className="cursor-pointer transition-colors"
      style={{
        padding: '8px 14px',
        margin: '0 2px',
        borderRadius: 6,
        background: isSelected ? 'var(--color-bg-card)' : undefined,
        outline: isSelected ? '1px solid var(--color-border-strong)' : undefined,
      }}
      onClick={onSelect}
      onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'var(--color-bg-secondary)' }}
      onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = '' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, marginTop: 4, backgroundColor: PRIORITY_COLOR[item.priority] ?? 'var(--color-fg-muted)' }} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: isSelected ? 500 : 400, lineHeight: 1.4, color: isSelected ? 'var(--color-fg)' : 'var(--color-fg-secondary)', minWidth: 0 }}>
          {item.title}
        </span>
        <ArrowRight className="w-3 h-3 flex-shrink-0 text-text-muted mt-0.5" style={{ opacity: isSelected ? 1 : 0.35 }} />
      </div>
    </div>
  )
}
